import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit3,
  FileText,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";

type Row = Record<string, string | number | boolean> & {
  id: number;
  status?: string;
};
type FormField = {
  label: string;
  key: string;
  type?: "text" | "date" | "time" | "number" | "color" | "select";
  options?: string[];
  full?: boolean;
  placeholder?: string;
};

const config = {
  screen: "payroll",
  title: "Payroll",
  singular: "Payroll Entry",
  search: "Search by payroll number, employee, ITS or month",
  columns: [
    ["Payroll No", "payrollNumber"],
    ["Employee", "employee"],
    ["ITS Number", "itsNumber"],
    ["Payroll Month", "month"],
    ["Gross Salary ($)", "gross"],
    ["Deductions ($)", "deductions"],
    ["Net Salary ($)", "net"],
    ["Payment Mode", "paymentMode"],
    ["Status", "status"],
  ],
  rows: [
    {
      id: 1,
      payrollNumber: "PAY-2026-0901",
      employee: "Ali Husain",
      itsNumber: "10021001",
      month: "September 2026",
      gross: "3,850.00",
      deductions: "320.00",
      net: "3,530.00",
      paymentMode: "Bank Transfer",
      status: "Processed",
    },
    {
      id: 2,
      payrollNumber: "PAY-2026-0902",
      employee: "Fatema Abbas",
      itsNumber: "10021018",
      month: "September 2026",
      gross: "3,420.00",
      deductions: "185.00",
      net: "3,235.00",
      paymentMode: "Bank Transfer",
      status: "Processed",
    },
    {
      id: 3,
      payrollNumber: "PAY-2026-0903",
      employee: "Mustafa Merchant",
      itsNumber: "10021032",
      month: "September 2026",
      gross: "2,950.00",
      deductions: "250.00",
      net: "2,700.00",
      paymentMode: "Cash",
      status: "Pending",
    },
    {
      id: 4,
      payrollNumber: "PAY-2026-0904",
      employee: "Zainab Jawad",
      itsNumber: "10021047",
      month: "September 2026",
      gross: "4,100.00",
      deductions: "410.00",
      net: "3,690.00",
      paymentMode: "Bank Transfer",
      status: "Processed",
    },
  ],
  description:
    "Create or process an employee payroll entry for the selected month.",
};
const formFieldSets: Record<string, FormField[]> = {
  payroll: [
    {
      label: "Payroll Number",
      key: "payrollNumber",
      placeholder: "e.g. PAY-2026-0905",
    },
    {
      label: "Employee Name",
      key: "employee",
      placeholder: "Enter employee name",
    },
    { label: "ITS Number", key: "itsNumber", placeholder: "Enter ITS number" },
    {
      label: "Payroll Month",
      key: "month",
      placeholder: "e.g. September 2026",
    },
    { label: "Gross Salary ($)", key: "gross", type: "number" },
    { label: "Deductions ($)", key: "deductions", type: "number" },
    { label: "Net Salary ($)", key: "net", type: "number" },
    {
      label: "Payment Mode",
      key: "paymentMode",
      type: "select",
      options: ["Bank Transfer", "Cash", "Cheque"],
    },
  ],
};
const screenFields = (screen: string): FormField[] =>
  formFieldSets[screen] || [];

const renderCell = (row: Row, key: string, columnIndex: number) => {
  const value = String(row[key] ?? "—");
  if (key === "status")
    return (
      <Badge
        className={
          ["Active", "Processed", "Approved"].includes(value)
            ? "border border-emerald-300 bg-emerald-50 text-emerald-700"
            : value === "Pending"
              ? "border border-amber-300 bg-amber-50 text-amber-700"
              : "border border-rose-300 bg-rose-50 text-rose-700"
        }
      >
        {value}
      </Badge>
    );
  if (key === "payrollNumber")
    return (
      <span className="font-mono font-semibold text-[#5B9BD5]">{value}</span>
    );
  if (key === "itsNumber")
    return <span className="font-mono text-sky-700">{value}</span>;
  if (key === "gross")
    return <span className="font-bold text-sky-700">${value}</span>;
  if (key === "deductions")
    return <span className="font-bold text-rose-600">${value}</span>;
  if (key === "net")
    return <span className="font-bold text-emerald-700">${value}</span>;
  if (columnIndex === 0)
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-[#5B9BD5]" />
        </div>
        <span>{value}</span>
      </div>
    );
  return value;
};

export function PayrollPage() {
  const { screen, description } = config;
  const fields = screenFields(screen);
  const { setPageHeader } = usePageHeader();
  const [rows, setRows] = useState<Row[]>(config.rows);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(config.columns[0][1]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<Row | null>(null);
  const [editing, setEditing] = useState<Row | null>(null);

  const [form, setForm] = useState<Row>({ id: 0, status: "Active" });


  useEffect(() => {
    setRows(config.rows);
    setSearch("");
    setSortKey(config.columns[0][1]);
    setPageHeader({ title: config.title, icon: <Users className="h-5 w-5" /> });
    return () => setPageHeader(null);
  }, [screen, setPageHeader]);


  
  const visible = useMemo(
    () =>
      rows
        .filter((row) =>
          Object.values(row)
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
        .sort(
          (a, b) =>
            String(a[sortKey] ?? "").localeCompare(
              String(b[sortKey] ?? ""),
              undefined,
              { numeric: true },
            ) * (sortDirection === "asc" ? 1 : -1),
        ),
    [rows, search, sortKey, sortDirection],
  );
  const pageRows = visible.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );


  const openForm = (row?: Row) => {
    const value = row ? { ...row } : { id: Date.now(), status: "Active" };
    fields.forEach(({ key, type }) => {
      if (value[key] === undefined) value[key] = "";
      if (type === "number") value[key] = String(value[key]).replace(/,/g, "");
    });
    setErrors({});
    setForm(value);
    setEditing(row || value);
  };



  const updateField = (key: string, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };


  const removePayroll = () => {
    if (!deleteTarget) return;
    setRows((current) => current.filter((row) => row.id !== deleteTarget.id));
    setCurrentPage((page) => Math.min(page, Math.max(1, Math.ceil((visible.length - 1) / pageSize))));
    setDeleteTarget(null);
    toast.success(`${config.singular} removed.`);
  };



  const save = () => {
    const nextErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const value = String(form[field.key] ?? "").trim();
      if (!value) {
        nextErrors[field.key] = `${field.label} is required.`;
      } else if (field.type === "number" && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
        nextErrors[field.key] = "Enter a non-negative number.";
      } else if (field.type === "select" && !field.options?.includes(value)) {
        nextErrors[field.key] = "Choose a valid option.";
      }
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setRows((current) =>
      current.some((row) => row.id === form.id)
        ? current.map((row) => (row.id === form.id ? form : row))
        : [form, ...current],
    );
    setEditing(null);
    toast.success(`${config.singular} saved successfully.`);
  };



  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={config.search}
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            className="text-white"
            style={{ background: "#5B9BD5" }}
            onClick={() => openForm()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add {config.singular}
          </Button>
        </div>
      </div>
      <Card
        className="bg-white border-[#5B9BD5]/30"
        style={{ gap: "0px", marginTop: "10px" }}
      >
        <CardContent style={{ padding: "0px 0px 10px 0px" }}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  {config.columns.map(([label, key]) => (
                    <TableHead
                      key={key}
                      style={{ color: "#5B9BD5" }}
                      className="whitespace-nowrap px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => {
                        if (sortKey === key)
                          setSortDirection((d) =>
                            d === "asc" ? "desc" : "asc",
                          );
                        else {
                          setSortKey(key);
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        {sortKey === key ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                  ))}
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-right"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow key={row.id} className="hover:bg-sky-50/30">
                    {config.columns.map(([, key], columnIndex) => (
                      <TableCell
                        key={key}
                        className="px-4 py-3 text-xs text-gray-700"
                      >
                        {renderCell(row, key, columnIndex)}
                      </TableCell>
                    ))}
                    <TableCell className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#5B9BD5] hover:bg-sky-50"
                          onClick={() => openForm(row)}
                          aria-label={`Edit payroll ${row.payrollNumber}`}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteTarget(row)}
                          aria-label={`Delete payroll ${row.payrollNumber}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={config.columns.length + 1}
                      className="text-center py-8 px-4 text-[#5B9BD5] font-medium"
                    >
                      No {config.title.toLowerCase()} found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <Pagination
          totalItems={visible.length}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          label={config.title.toLowerCase()}
        />
      </Card>
      <Dialog
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
      >
        <DialogContent className="w-[60vw] !max-w-[60vw] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              {rows.some((row) => row.id === form.id) ? "Edit" : "Add New"}{" "}
              {config.singular}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 px-1 py-4 pb-24 sm:grid-cols-2 sm:pb-4">
            {fields.map((field) => (
              <div
                key={field.key}
                className={
                  field.full
                    ? "sm:col-span-2"
                    : fields.every((item) => !item.full) && fields.length <= 3
                      ? "sm:col-span-2"
                      : ""
                }
              >
                {field.type === "select" ? (
                  <SelectFieldset label={field.label} required>
                    <Select
                      value={String(form[field.key] ?? "")}
                      onValueChange={(value) => updateField(field.key, value)}
                    >
                      <SelectTriggerUI aria-label={field.label} aria-describedby={errors[field.key] ? `${field.key}-error` : undefined}>
                        <SelectValue
                          placeholder={
                            field.placeholder || `Choose ${field.label}`
                          }
                        />
                      </SelectTriggerUI>
                      <SelectContent>
                        {field.options?.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                ) : (
                  <InputFieldset
                    label={field.label}
                    aria-label={field.label}
                    aria-describedby={errors[field.key] ? `${field.key}-error` : undefined}
                    required
                    type={field.type || "text"}
                    value={String(form[field.key] ?? "")}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    min={field.type === "number" ? 0 : undefined}
                    step={field.type === "number" ? "any" : undefined}
                    placeholder={field.placeholder}
                  />
                )}
                {errors[field.key] && (
                  <p id={`${field.key}-error`} className="mt-1 text-xs text-red-600" role="alert">{errors[field.key]}</p>
                )}
              </div>
            ))}
            <div className="sm:col-span-2">
              <SelectFieldset label="Status">
                <Select
                  value={String(form.status || "Active")}
                  onValueChange={(value) =>
                    setForm((current) => ({ ...current, status: value }))
                  }
                >
                  <SelectTriggerUI>
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </SelectFieldset>
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-white border-t sm:border-0 px-6 py-4 sm:p-0 sm:static flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              style={{ background: "#5B9BD5" }}
              className="text-white w-full sm:w-auto"
              onClick={save}
            >
              {rows.some((row) => row.id === form.id) ? "Update" : "Add"}{" "}
              {config.singular}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Payroll Entry"
        description={<>Delete payroll “{deleteTarget?.payrollNumber}” for {deleteTarget?.employee}?</>}
        confirmText="Delete"
        onConfirm={removePayroll}
      />
    </div>
  );
}
